import { z } from 'zod';
import { Currency } from './enums.js';

/**
 * Decimal-string primitive. Строковое представление десятичного числа
 * (regex: опциональный минус + цифры + опционально дробная часть).
 * Canonical-форма для передачи Decimal-значений через JSON без потери точности.
 */
export const DecimalString = z.string().regex(/^-?\d+(\.\d+)?$/, 'invalid decimal string');
export type DecimalString = z.infer<typeof DecimalString>;

/**
 * W13.1 — Positive decimal (без минуса). Для qty_to_produce, planned_qty,
 * time_minutes — ситуации где негативное значение — валидационная ошибка.
 */
export const PositiveDecimalString = z
  .string()
  .regex(/^\d+(\.\d+)?$/, 'invalid positive decimal string');
export type PositiveDecimalString = z.infer<typeof PositiveDecimalString>;

/**
 * W13.1 — Signed decimal (явно допускает минус). Для qty_delta в SLE,
 * variance_amount. Алиас DecimalString — добавлен для явности.
 */
export const SignedDecimalString = DecimalString;
export type SignedDecimalString = z.infer<typeof SignedDecimalString>;

/**
 * MoneyAmount — обязательная форма для всех денежных полей в API.
 * Никогда не использовать bare decimal-string для денег — только MoneyAmount.
 *
 * W13.2 — `currency` принимается в любом регистре, но нормализуется в uppercase
 * на границе парсинга (канонический вид: 'KZT', 'USD', 'EUR').
 */
export const MoneyAmount = z
  .object({
    amount: DecimalString,
    currency: z.preprocess((v) => (typeof v === 'string' ? v.toUpperCase() : v), Currency),
  })
  .strict();
export type MoneyAmount = z.infer<typeof MoneyAmount>;

export const UuidString = z.string().uuid();
export const IsoDateTime = z.string().datetime({ offset: true });
export const IsoDate = z.string().date();

/**
 * WTP-B1 (tails-sweep-2) — булев параметр, приходящий строкой.
 *
 * **Почему нельзя `z.coerce.boolean()`.** Coercion делает `Boolean(value)`, а
 * `Boolean('false') === true` — любая непустая строка истинна. Значит
 * `?include_archived=false` ВКЛЮЧАЛ архивные: параметр просили выключить, а он
 * включался. Схема при этом валидна, тип верный, ошибки нет — расхождение видно
 * только по данным. Так и было поймано (FND-16 пака `work-tasks-performance`):
 * `?include_total=false` продолжал считать COUNT.
 *
 * Класс, а не место: в проекте таких параметров были 25 в query-схемах
 * бэкенда, фронта и общих контрактов, а точечная заплатка стояла ровно в одном
 * файле. Поэтому helper общий, и на возвращение `z.coerce.boolean()` стоит
 * барьер `scripts/lint/no-coerce-boolean.mjs`.
 *
 * Правила чтения:
 *   `'false'` / `'0'` / `''` → `false`; `'true'` / `'1'` → `true`;
 *   булев — как есть; всё прочее — ошибка валидации, а не молчаливое `true`.
 */
export const QueryBool = z.preprocess((v) => {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === '1') return true;
    if (s === 'false' || s === '0' || s === '') return false;
  }
  return v;
}, z.boolean());
export type QueryBool = z.infer<typeof QueryBool>;
